import { PersistentKeyboardView } from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import type { ColorValue } from "@-/com.npmjs.react-native";
import { View } from "@-/com.npmjs.react-native";
import { useEffect } from "react";
import { useNavigation } from "@-/com.npmjs.expo-router";
import {
  persistentKeyboardDismiss,
  persistentKeyboardOnKeyPress,
} from "#persistent-keyboard-runtime";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { cn } from "@-/com.npmjs.cn";

const inputTextPaddingLeft = 8;
const UniwindPersistentKeyboardView = withUniwind(PersistentKeyboardView);

/**
 * `text` and `mostRecentEventCount` travel as a pair or not at all.
 *
 * Supplying `text` without the count would leave the native side comparing against a
 * permanently stale counter and silently ignoring every write; supplying it with a count from
 * a different render would let a stale write through and delete typed characters. Omitting
 * both opts out of controlling the field entirely.
 */
type CollapsiblePersistentKeyboardTextProps =
  | {
      /** Third element of the {@link usePersistentKeyboardText} tuple, same render as `text`. */
      mostRecentEventCount: number;
      /** Live text to display, i.e. the first element of that same tuple. */
      text: string;
    }
  | {
      mostRecentEventCount?: undefined;
      /**
       * Omitted for a focus-anchor mount (typically with `hideInput`): the keyboard is rendered
       * only so the module has a view to bind first-responder to, and must never write to the
       * field — a hardcoded `text=""` here used to wipe text the user had already typed.
       */
      text?: undefined;
    };

export type CollapsiblePersistentKeyboardProps = {
  color: ColorValue;
  fontFamily?: string;
  fontSize?: number;
  hideInput?: boolean;
  /** Clears the placeholder while the field is focused instead of showing it until text is typed. */
  hidePlaceholderWhileFocused?: boolean;
  onBlur: () => boolean;
  onInput?: (event: { key: string }) => void;
  onUnmount: () => boolean;
  placeholder: string;
  placeholderTextColor: ColorValue;
  /** Left inset for typed text; defaults to 8. Pass 0 when the parent already pads the input row. */
  textPaddingLeft?: number;
} & CollapsiblePersistentKeyboardTextProps;

export function CollapsiblePersistentKeyboard(props: CollapsiblePersistentKeyboardProps) {
  const {
    color,
    fontFamily = "Han Composite",
    fontSize = 17,
    hideInput = false,
    hidePlaceholderWhileFocused = false,
    mostRecentEventCount,
    onInput,
    onUnmount,
    placeholder,
    placeholderTextColor,
    text,
    textPaddingLeft = inputTextPaddingLeft,
  } = props;
  const navigation = useNavigation();

  useEffect(() => {
    const keyPressSub = persistentKeyboardOnKeyPress((event) => {
      onInput?.(event);
    });
    const blurSub = navigation.addListener("blur", () => {
      const shouldPersist = onUnmount();
      if (!shouldPersist) {
        persistentKeyboardDismiss();
      }
    });
    return () => {
      keyPressSub.remove();
      blurSub();
    };
  }, [navigation, onInput, onUnmount]);

  return (
    <View
      className={cn(
        hideInput && "absolute size-0 overflow-hidden opacity-0",
        !hideInput && "flex-1",
      )}
      pointerEvents="box-none"
    >
      <UniwindPersistentKeyboardView
        className={hideInput ? "size-0" : "flex-1"}
        color={color}
        fontFamily={fontFamily}
        fontSize={fontSize}
        hidePlaceholderWhileFocused={hidePlaceholderWhileFocused}
        mostRecentEventCount={mostRecentEventCount}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        text={text}
        textPaddingLeft={textPaddingLeft}
      />
    </View>
  );
}
